import { Slot } from '@radix-ui/react-slot'
import { Children, forwardRef, isValidElement } from 'react'
import { tv, type VariantProps } from 'tailwind-variants'

import { Spinner } from './spinner'

const DISABLEABLE_ELEMENTS = new Set(['button', 'input', 'select', 'textarea', 'option', 'optgroup', 'fieldset'])

const buttonRecipe = tv({
  base: [
    'relative inline-flex items-center justify-center whitespace-nowrap rounded-md font-normal',
    'transition-colors cursor-pointer',
    'disabled:cursor-not-allowed',
    'no-underline hover:no-underline',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring',
    '[&_svg]:shrink-0 [&_svg]:grow-0 [&_svg]:stroke-[1.5]',
  ],
  variants: {
    variant: {
      solid: 'disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted',
      subtle:
        'border disabled:bg-muted/40 disabled:border-muted disabled:text-muted-foreground disabled:hover:bg-muted/40',
      outline:
        'border bg-transparent disabled:border-muted disabled:text-muted-foreground disabled:hover:bg-transparent',
      ghost: 'bg-transparent disabled:text-muted-foreground disabled:hover:bg-transparent',
      link: 'bg-transparent underline-offset-[0.15rem] hover:underline disabled:hover:no-underline disabled:text-foreground/20',
      ajax: 'bg-transparent underline underline-offset-[0.15rem] decoration-dashed decoration-[0.05rem] hover:decoration-solid disabled:text-foreground/20',
    },
    size: {
      dense: 'h-7 px-4 gap-1 [&_svg]:w-4 [&_svg]:h-4',
      xs: 'h-7 px-4 text-sm gap-1',
      sm: 'h-8 px-5 text-sm gap-1.5 [&_svg]:w-5 [&_svg]:h-5',
      md: 'h-9 px-6 gap-1.5 text-sm',
    },
    colorPalette: {
      neutral: '',
      primary: '',
      warning: '',
      success: '',
      error: '',
    },
  },
  compoundVariants: [
    // Solid variants
    {
      variant: 'solid',
      colorPalette: 'neutral',
      class: 'bg-foreground text-white hover:bg-foreground/80',
    },
    {
      variant: 'solid',
      colorPalette: 'primary',
      class: 'bg-primary text-primary-foreground hover:bg-primary/80',
    },
    {
      variant: 'solid',
      colorPalette: 'warning',
      class: 'bg-warning text-warning-foreground hover:bg-warning/80',
    },
    {
      variant: 'solid',
      colorPalette: 'success',
      class: 'bg-success text-success-foreground hover:bg-success/80',
    },
    {
      variant: 'solid',
      colorPalette: 'error',
      class: 'bg-destructive text-destructive-foreground hover:bg-destructive/80',
    },

    // Subtle variants
    {
      variant: 'subtle',
      colorPalette: 'neutral',
      class: 'bg-accent/50 border-border text-secondary-foreground hover:bg-zinc-200',
    },
    {
      variant: 'subtle',
      colorPalette: 'primary',
      class: 'bg-muted-primary border-primary/40 text-primary hover:bg-accent-primary',
    },
    {
      variant: 'subtle',
      colorPalette: 'warning',
      class: 'bg-muted-warning border-warning/40 text-warning hover:bg-accent-warning',
    },
    {
      variant: 'subtle',
      colorPalette: 'success',
      class: 'bg-muted-success border-success/30 text-success hover:bg-accent-success',
    },
    {
      variant: 'subtle',
      colorPalette: 'error',
      class: 'bg-muted-destructive border-destructive/30 text-destructive hover:bg-accent-destructive',
    },

    {
      variant: 'outline',
      colorPalette: 'neutral',
      class: 'border-border text-secondary-foreground hover:bg-zinc-100',
    },
    {
      variant: 'outline',
      colorPalette: 'primary',
      class: 'border-sky-200 text-primary hover:bg-sky-50',
    },
    {
      variant: 'outline',
      colorPalette: 'warning',
      class: 'border-amber-200 text-warning hover:bg-amber-50',
    },
    {
      variant: 'outline',
      colorPalette: 'success',
      class: 'border-green-200 text-green-600 hover:bg-green-50',
    },
    {
      variant: 'outline',
      colorPalette: 'error',
      class: 'border-red-200 text-red-600 hover:bg-red-50',
    },

    // Ghost variants
    {
      variant: 'ghost',
      colorPalette: 'neutral',
      class: 'text-secondary-foreground hover:bg-zinc-100',
    },
    {
      variant: 'ghost',
      colorPalette: 'primary',
      class: 'text-primary hover:bg-muted-primary',
    },
    {
      variant: 'ghost',
      colorPalette: 'warning',
      class: 'text-warning hover:bg-muted-warning',
    },
    {
      variant: 'ghost',
      colorPalette: 'success',
      class: 'text-success hover:bg-muted-success',
    },
    {
      variant: 'ghost',
      colorPalette: 'error',
      class: 'text-destructive hover:bg-muted-destructive',
    },

    // Link variants
    { variant: 'link', colorPalette: 'neutral', class: 'text-secondary-foreground' },
    { variant: 'link', colorPalette: 'primary', class: 'text-primary' },
    { variant: 'link', colorPalette: 'warning', class: 'text-warning' },
    { variant: 'link', colorPalette: 'success', class: 'text-success' },
    { variant: 'link', colorPalette: 'error', class: 'text-destructive' },

    // Ajax variants
    { variant: 'ajax', colorPalette: 'neutral', class: 'text-secondary-foreground' },
    { variant: 'ajax', colorPalette: 'primary', class: 'text-primary' },
    { variant: 'ajax', colorPalette: 'warning', class: 'text-warning' },
    { variant: 'ajax', colorPalette: 'success', class: 'text-success' },
    { variant: 'ajax', colorPalette: 'error', class: 'text-destructive' },

    // Link & Ajax size overrides (remove padding and fixed height)
    { variant: ['link', 'ajax'], size: ['dense', 'xs', 'sm', 'md'], class: 'h-auto px-0' },
  ],
  defaultVariants: {
    variant: 'solid',
    size: 'md',
    colorPalette: 'neutral',
  },
})

interface ButtonLoadingProps {
  loading?: boolean
  loadingText?: React.ReactNode
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonRecipe>, ButtonLoadingProps {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { asChild, loading, disabled, loadingText, children, variant, size, colorPalette, className, ...rest } = props

  const trulyDisabled = loading || disabled
  const Comp = asChild ? Slot : 'button'

  const canPassDisabled = () => {
    if (!asChild) return true
    try {
      const child = Children.only(children)
      return isValidElement(child) && typeof child.type === 'string' && DISABLEABLE_ELEMENTS.has(child.type)
    } catch {
      return false
    }
  }

  return (
    <Comp
      ref={ref}
      disabled={canPassDisabled() ? trulyDisabled : undefined}
      aria-disabled={trulyDisabled}
      className={buttonRecipe({ variant, size, colorPalette, className })}
      {...rest}
    >
      {!loading ? (
        children
      ) : loadingText ? (
        loadingText
      ) : (
        <>
          <ButtonSpinner />
          <span className='opacity-0'>{children}</span>
        </>
      )}
    </Comp>
  )
})

Button.displayName = 'Button'

const ButtonSpinner = () => (
  <div className='inline-grid place-items-center absolute inset-0'>
    <Spinner size='sm' />
  </div>
)
